import React from "react";
import { CheckCircle2, Circle, ArrowRight, Minus, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useOnboardingExperience } from "./OnboardingExperienceProvider";

const STEP_LINKS = {
  client: "/peoples/createClient",
  crew: "/peoples/createEmp",
  shift: "/operations/appoinmentSchedule",
  profile: "/account/profile",
  dashboard: "/dashboard",
};

const formatFieldList = (fields = []) => {
  if (fields.length <= 1) {
    return fields[0] || "";
  }

  if (fields.length === 2) {
    return `${fields[0]} and ${fields[1]}`;
  }

  return `${fields.slice(0, -1).join(", ")}, and ${fields[fields.length - 1]}`;
};

const buildModalSteps = (progress, hasOptionalProfileFieldsMissing, optionalProfileFieldLabels) => [
  {
    id: "profileComplete",
    title: "Company profile complete",
    completed: progress.profileComplete,
    actionLabel: hasOptionalProfileFieldsMissing ? "Review profile" : null,
    path: hasOptionalProfileFieldsMissing ? STEP_LINKS.profile : null,
    helperText: hasOptionalProfileFieldsMissing
      ? `Optional but recommended: add your ${formatFieldList(optionalProfileFieldLabels)} on the profile page.`
      : null,
  },
  {
    id: "clientAdded",
    title: "Add your first client",
    completed: progress.clientAdded,
    actionLabel: "Add client",
    path: STEP_LINKS.client,
    helperText: null,
  },
  {
    id: "crewAdded",
    title: "Add a crew member",
    completed: progress.crewAdded,
    actionLabel: "Invite crew",
    path: STEP_LINKS.crew,
    helperText: null,
  },
  {
    id: "shiftScheduled",
    title: "Schedule your first shift",
    completed: progress.shiftScheduled,
    actionLabel: "Create appointment",
    path: STEP_LINKS.shift,
    helperText: null,
  },
];

export default function OnboardingModal() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:767px)");
  const {
    progress,
    hasShiftsBooked,
    isModalOpen,
    closeModal,
    dismissForSession,
    modalCompletedSteps,
    modalTotalSteps,
    modalProgressPercentage,
    isCompanyUser,
    loading,
    optionalProfileFieldLabels,
    hasOptionalProfileFieldsMissing,
  } = useOnboardingExperience();

  if (!isCompanyUser || loading) {
    return null;
  }

  const steps = buildModalSteps(
    progress,
    hasOptionalProfileFieldsMissing,
    optionalProfileFieldLabels
  );
  const isModalComplete = modalCompletedSteps === modalTotalSteps;

  const handleNavigate = (path) => {
    dismissForSession();
    navigate(path);
  };

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={(open) => {
        if (!open) {
          closeModal();
        }
      }}
    >
      <DialogContent
        data-onboarding-modal="true"
        closeButton={false}
        closeOnOutsideClick={!isMobile}
        overlayClassName={isMobile ? "bg-black/30" : undefined}
        className={`flex flex-col overflow-hidden border-0 bg-white p-0 shadow-2xl dark:bg-[#111827] ${
          isMobile
            ? "top-[56%] h-auto max-h-[74dvh] w-[calc(100vw-28px)] max-w-none translate-x-[-50%] translate-y-[-50%] rounded-2xl pb-[env(safe-area-inset-bottom)]"
            : "max-h-[90vh] sm:max-w-[680px]"
        }`}
      >
        <DialogHeader className="space-y-3">
          <div className="border-b border-slate-200 px-5 pb-5 pt-6 dark:border-slate-800 sm:px-6">
            <div className="mb-4 flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={closeModal}
                className={`text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white ${
                  isMobile ? "h-9 px-3" : "h-8 w-8 p-0"
                }`}
                aria-label="Minimize setup guide"
              >
                <Minus className="h-4 w-4" />
                {isMobile && <span className="ml-1 text-xs font-medium">Minimize</span>}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={dismissForSession}
                className={`text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white ${
                  isMobile ? "h-9 px-3" : "h-8 w-8 p-0"
                }`}
                aria-label="Close setup guide for this session"
              >
                <X className="h-4 w-4" />
                {isMobile && <span className="ml-1 text-xs font-medium">Close</span>}
              </Button>
            </div>
            <DialogTitle className="text-2xl font-semibold text-slate-900 dark:text-white">
              {isModalComplete
                ? "You're ready to go"
                : hasShiftsBooked
                ? "Don’t forget to update your profile"
                : "Get your first shift live"}
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {isModalComplete
                ? "Your first shift is scheduled. You'll see it live on your dashboard."
                : hasShiftsBooked
                ? "You've already scheduled shifts! Finish these remaining steps to complete your setup."
                : "Most operators are running their first shift within 20 minutes. Here's how."}
            </DialogDescription>

            <div className="mt-5">
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                <div
                  style={{
                    width: isModalComplete ? "100%" : `${modalProgressPercentage}%`,
                  }}
                  className="h-full rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-violet-500 transition-all dark:from-cyan-300 dark:via-sky-400 dark:to-indigo-400"
                />
              </div>
              <p className="mt-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                {modalCompletedSteps} of {modalTotalSteps} complete
              </p>
            </div>
          </div>
        </DialogHeader>

        {!isModalComplete ? (
          <div className="flex-1 space-y-3 overflow-y-auto px-5 py-5 sm:px-6">
            {steps.map((step) => {
              const Icon = step.completed ? CheckCircle2 : Circle;
              const shouldShowAction = Boolean(step.actionLabel) && (!step.completed || step.helperText);

              return (
                <div
                  key={step.id}
                  className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-4 dark:border-slate-800 dark:bg-slate-900/40 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <Icon
                      size={20}
                      style={{ marginTop: "2px", flexShrink: 0 }}
                      className={
                        step.completed
                          ? "text-green-600 dark:text-green-400"
                          : "text-slate-400 dark:text-slate-500"
                      }
                    />
                    <div className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {step.title}
                      </span>
                      {step.helperText && (
                        <span className="mt-1 block text-xs font-medium text-amber-700 dark:text-amber-300">
                          {step.helperText}
                        </span>
                      )}
                      {step.id === "shiftScheduled" && !step.completed && (
                        <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                          This opens the existing create appointment flow.
                        </span>
                      )}
                    </div>
                  </div>

                  {shouldShowAction ? (
                    <Button
                      type="button"
                      onClick={() => handleNavigate(step.path)}
                      className="w-full bg-[#078037] text-white hover:bg-[#05662a] sm:w-auto"
                    >
                      {step.actionLabel}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  ) : (
                    <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                      {step.completed ? "Complete" : "Pending"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <DialogFooter className="px-5 pb-6 pt-2 sm:justify-start sm:px-6">
            <Button
              type="button"
              onClick={() => handleNavigate(STEP_LINKS.dashboard)}
              className="bg-[#078037] text-white hover:bg-[#05662a]"
              fullWidth
            >
              Go to Dashboard
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
